// @ts-nocheck
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { rleventsSupabase } from '@/integrations/rlevents/client';
import { supabase as recruitlookSupabase } from '@/integrations/supabase/client';

interface UseRleventsSessionResult {
  session: Session | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const isSessionValid = (session: Session | null): boolean => {
  if (!session) return false;
  const expiresAt = session.expires_at;
  if (!expiresAt) return true;
  // expires_at is in seconds
  return expiresAt * 1000 > Date.now() + 5_000;
};

export function useRleventsSession(): UseRleventsSessionResult {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [rlUserId, setRlUserId] = useState<string | null>(null);
  const hasLoggedResponseRef = useRef(false);

  // Track the current RL user id so we can re-run the handshake when it changes.
  useEffect(() => {
    let mounted = true;

    recruitlookSupabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setRlUserId(data?.session?.user?.id ?? null);
    });

    const { data: sub } = recruitlookSupabase.auth.onAuthStateChange((_event, newSession) => {
      setRlUserId(newSession?.user?.id ?? null);
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe();
    };
  }, []);

  const bridgeNewSession = useCallback(async (rlAccessToken: string): Promise<void> => {
    const rleventsUrl = import.meta.env.VITE_RLEVENTS_SUPABASE_URL;
    const rleventsAnonKey = import.meta.env.VITE_RLEVENTS_SUPABASE_ANON_KEY;

    const res = await fetch(`${rleventsUrl}/functions/v1/sso-sign-in`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${rlAccessToken}`,
        apikey: rleventsAnonKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });

    const payload = await res.json().catch(() => null);

    if (!hasLoggedResponseRef.current) {
      // eslint-disable-next-line no-console
      console.log('[useRleventsSession] sso-sign-in response:', payload);
      hasLoggedResponseRef.current = true;
    }

    if (!res.ok || !payload) {
      throw new Error(payload?.error || `sso-sign-in failed (${res.status})`);
    }

    const email: string | undefined =
      payload.email || payload?.properties?.email || payload?.user?.email;

    const tokenHash: string | undefined =
      payload.token_hash ||
      payload.hashed_token ||
      payload?.properties?.hashed_token ||
      payload?.properties?.token_hash;

    const type: string = payload.type || payload?.properties?.type || 'magiclink';

    if (!tokenHash || !email) {
      // eslint-disable-next-line no-console
      console.log('[useRleventsSession] Unexpected sso-sign-in response shape:', payload);
      throw new Error('Unexpected sso-sign-in response shape');
    }

    const { data: verifyData, error: verifyError } = await rleventsSupabase.auth.verifyOtp({
      token_hash: tokenHash,
      type: type as any,
      email,
    } as any);

    if (verifyError) {
      throw new Error(verifyError.message);
    }

    setSession(verifyData?.session ?? null);
  }, []);

  const runHandshake = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // 1. Get current RL session
      const { data: rlData } = await recruitlookSupabase.auth.getSession();
      const rlSession = rlData?.session;

      if (!rlSession?.access_token || !rlSession?.user?.id) {
        // No RL user — make sure RLE is also signed out.
        const { data: existing } = await rleventsSupabase.auth.getSession();
        if (existing?.session) {
          try {
            await rleventsSupabase.auth.signOut();
          } catch {
            /* no-op */
          }
        }
        try {
          localStorage.removeItem('rlevents-auth');
        } catch {
          /* no-op */
        }
        setSession(null);
        setError('Not signed in to RecruitLook');
        setLoading(false);
        return;
      }

      // 2. Inspect cached RLE session
      const { data: existing } = await rleventsSupabase.auth.getSession();
      const cached = existing?.session ?? null;

      // 3. Validate: same user.id AND not expired → trust it
      if (
        cached &&
        isSessionValid(cached) &&
        cached.user?.id &&
        cached.user.id === rlSession.user.id
      ) {
        setSession(cached);
        setLoading(false);
        return;
      }

      // 4. Stale or wrong-user RLE session → invalidate before bridging
      if (cached) {
        try {
          await rleventsSupabase.auth.signOut();
        } catch {
          /* no-op */
        }
      }
      try {
        localStorage.removeItem('rlevents-auth');
      } catch {
        /* no-op */
      }

      // 5. Bridge a fresh RLE session via sso-sign-in
      await bridgeNewSession(rlSession.access_token);
      setLoading(false);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[useRleventsSession] handshake error:', err);
      setSession(null);
      setError(err?.message || 'Unknown error during RLevents SSO handshake');
      setLoading(false);
      // Intentionally no auto-retry; rely on remount/refresh.
    }
  }, [bridgeNewSession]);

  useEffect(() => {
    runHandshake();

    const { data: sub } = rleventsSupabase.auth.onAuthStateChange((_event, newSession) => {
      // Only mirror changes that match the current RL user; otherwise let the
      // handshake decide whether to invalidate.
      if (!newSession) {
        setSession(null);
        return;
      }
      if (rlUserId && newSession.user?.id === rlUserId) {
        setSession(newSession);
      }
    });

    return () => {
      sub?.subscription?.unsubscribe();
    };
    // Re-run when the RL user identity changes (login, logout, switch user).
  }, [runHandshake, rlUserId]);

  return { session, loading, error, refresh: runHandshake };
}
